import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Route, RoutePlanElement, RoutePlanType } from 'src/app/models/route';
import { LanguageService } from 'src/app/services/account/language.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { TurnValueIntegerThousandsPipe } from 'src/app/pipes/turn-value-integer-thousands.pipe';
import { InfoDialogComponent } from '../../../modals/info-dialog/info-dialog.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { SvgRecolorDirective } from 'src/app/directives/svg-recolor.directive';

export interface RoutePlanClickEvent {
  routeEl: RoutePlanElement;
  index: number;
}

@Component({
  standalone: true,
  selector: 'app-route-plan',
  templateUrl: './route-plan.component.html',
  styleUrls: ['./route-plan.component.scss'],
  imports: [NgTemplateOutlet, TurnValueIntegerThousandsPipe, SvgRecolorDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoutePlanComponent {
  private static nextId = 0;

  /** Prefix of the per-card battery gradient ids; SVG ids are document-global, so it is unique per instance. */
  public readonly batteryGradientId = `re-bat-charge-${RoutePlanComponent.nextId++}-`;

  @Input() routeData: Route | null = null;
  @Input() loadingMap: string = '';
  @Input() noRoute: boolean = false;
  @Input() activePlanningPanelRouteElement: number = -1;

  @Output() routePlanClick = new EventEmitter<RoutePlanClickEvent>();

  constructor(
    public settingsService: SettingsService,
    public utilsService: UtilsService,
    public languageService: LanguageService,
    private readonly snackBar: MatSnackBar,
    private readonly stackedModalsService: StackedModalsService,
    private readonly vehicleService: VehicleService
  ) { }

  public get RoutePlanType(): typeof RoutePlanType {
    return RoutePlanType;
  }

  public onRoutePlanClick(routeEl: RoutePlanElement, index: number): void {
    this.routePlanClick.emit({ routeEl, index });
  }

  public getChargeTime(chargeTimeHour: number, chargeTimeMin: number): string {
    return this.utilsService.getChargeTime(chargeTimeHour, chargeTimeMin);
  }

  public isFastchargeIcon(maxPercent: number): boolean {
    const fastChargeLimit = this.vehicleService.getFastChargeLimit();
    if (fastChargeLimit) {
      return fastChargeLimit < maxPercent && this.vehicleService.getSelectedCar().FastChargePower != 0;
    }
    return false;
  }

  public openBatteryReservedSnackbar(): void {
    this.snackBar.open(this.languageService.languageJSON.WarningSoc_popup, "", { duration: 5000, panelClass: 'snackbar-warning' });
  }

  public openFastchargeDialog(event: Event): void {
    event.stopPropagation();
    this.stackedModalsService.openModal(InfoDialogComponent,
      { data: { message: this.languageService.languageJSON.InfoSidebar_Info_FastChargeLimit }, autoFocus: false });
  }

  /** Where the charge starts within the departure fill (0..1); the battery gradient runs from there. */
  public getChargeStartOffset(el: RoutePlanElement): number {
    const max = el.BatteryMaxPercent ?? 0;
    return max > 0 ? Math.min(1, Math.max(0, (el.BatteryMinPercent ?? 0) / max)) : 0;
  }

  public getChargeTimeLabel(el: RoutePlanElement): string | null {
    if (el.ChargeTimeHour == null && el.ChargeTimeMin == null) return null;
    return this.getChargeTime(el.ChargeTimeHour ?? 0, el.ChargeTimeMin ?? 0);
  }

  /** Plug and power for the charging line; missing ones are left out instead of leaving a stray separator. */
  public getChargeDetails(el: RoutePlanElement): string[] {
    const power = el.ChargePower ? `${el.ChargePower} kW` : '';
    return [this.getPlugLabel(el.Icon), power].filter(Boolean);
  }

  public getPlugLabel(icon: string | null): string {
    if (!icon) return '';
    const map: Record<string, string> = {
      'plug_ccs.svg': 'CCS',
      'plug_chademo.svg': 'CHAdeMO',
      'plug_type2.svg': 'Type 2',
      'plug_type1.svg': 'Type 1',
      'plug_supercharger.svg': 'Supercharger',
      'plug_schuko.svg': 'Schuko',
    };
    return map[icon] ?? icon.replace('plug_', '').replace('.svg', '').toUpperCase();
  }
}
